import { Ajax } from "../Common/Ajax";
import { GlobalData } from "../Common/GlobalData";
import { HtmlHelpers } from "../Common/HtmlHelpers";
import { ChartAxes, TemplateHelpers } from "../Common/KendoHelpers";

export class RelativeStrengthLine {
    private static readonly REPORT_DATE = '#reportDate';
    private static readonly SYMBOL = '#symbol';
    private static readonly COMPANY_NAME = '#companyName';
    private static readonly NUM_WEEKS = '[name=numWeeks]';
    private static readonly CPR_STRENGTH = '#cprStrength';
    private static readonly SNP_500_PCT_PRICE_INCREASE = '#snp500PctPriceIncrease';
    private static readonly PCT_PRICE_INCREASE = '#pctPriceIncrease';
    private static readonly CHART = '#chart';
    private static readonly GRID = '#grid';

    private dataSourceOptions!: kendo.data.DataSourceOptions;

    constructor(symbol: string) {
        this.init(symbol);
    }

    private init(symbol: string) {
        const symbolElement = $(RelativeStrengthLine.SYMBOL);
        symbolElement.kendoMaskedTextBox();
        if (symbol && symbol.length > 0) {
            $(RelativeStrengthLine.SYMBOL).val(symbol.toUpperCase());
        }

        $(RelativeStrengthLine.SYMBOL).on('keyup', (event: any) => {
            if (HtmlHelpers.enterKeyPressed(event)) {
                this.refreshGrid();
            }
        });

        $(RelativeStrengthLine.NUM_WEEKS).on('change', () => {
            this.refreshGrid();
        });

        this.setDataSourceOptions(symbol);
        if (symbol) {
            this.updateCompanyName(symbol);
        }

        $(RelativeStrengthLine.GRID).kendoGrid({
            dataSource: new kendo.data.DataSource(this.dataSourceOptions),
            height: 915,
            groupable: false,
            pageable: false,
            dataBound: () => {
                const data = $(RelativeStrengthLine.GRID).data('kendoGrid')!.dataSource.data();
                if (!data || data.length == 0) {
                    return;
                }

                // Display report date as the most recent close date.
                $(RelativeStrengthLine.REPORT_DATE).html(
                    HtmlHelpers.getFormattedDate(data[0].date));

                // Calculate and display the change price relative strength.
                let aboveRs = 0;
                let belowRs = 0;
                for (let i = 0; i < data.length; ++i) {
                    const row = data[i];
                    if (row.changePriceRelative) {
                        row.changePriceRelative < 0 ? ++belowRs : ++aboveRs;
                    }
                }
                const cprStrength = Math.round(10000 * aboveRs / (aboveRs + belowRs)) / 100;
                $(RelativeStrengthLine.CPR_STRENGTH).html(String(cprStrength) + '%');

                // Calculate and display the percent price increases.
                const pctPriceIncrease = Math.round(10000 *
                    (data[0].stockClose - data[data.length - 1].stockClose) /
                    data[data.length - 1].stockClose) / 100;
                $(RelativeStrengthLine.PCT_PRICE_INCREASE).html(String(pctPriceIncrease) + '%');
                const snp500PctPriceIncrease = Math.round(10000 *
                    (data[0].indexClose - data[data.length - 1].indexClose) /
                    data[data.length - 1].indexClose) / 100;
                $(RelativeStrengthLine.SNP_500_PCT_PRICE_INCREASE).html(
                    String(snp500PctPriceIncrease) + '%');

                this.initChart(data);
            },
            columns: [{
                field: 'date',
                title: 'Date',
                width: '10%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getFormattedDateTemplate('date')),
            }, {
                field: 'indexHigh',
                title: 'S&P500<br>High',
                width: '10%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:c2}',
            }, {
                field: 'indexClose',
                title: 'S&P500<br>Close',
                width: '10%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:c2}',
            }, {
                field: 'indexVolume',
                title: 'S&P500<br>Volume',
                width: '12%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:n0}',
            }, {
                field: 'stockHigh',
                title: 'High',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:c2}',
            }, {
                field: 'stockClose',
                title: 'Close',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:c2}',
            }, {
                field: 'stockVolume',
                title: 'Volume',
                width: '12%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:n0}',
            }, {
                field: 'priceRelative',
                title: 'Price<br>Relative',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                format: '{0:p2}',
            }, {
                field: 'changePriceRelative',
                title: 'Change Price<br>Relative',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getBgColorPercentTemplate('changePriceRelative', 2)),
            }, {
                field: 'rsLineNewHigh',
                title: 'RS Line<br>New High',
                width: '6%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell grid-cell-center' },
                template: kendo.template(TemplateHelpers.getGreenBoolTemplate('rsLineNewHigh')),
            }]
        });
    }

    private refreshGrid() {
        const symbol = $(RelativeStrengthLine.SYMBOL).val()?.toString() ?? '';
        $(RelativeStrengthLine.SYMBOL).val(symbol.toUpperCase());

        if (symbol && symbol.length > 0) {
            this.updateCompanyName(symbol);
            this.setDataSourceOptions(symbol);
            const grid = $(RelativeStrengthLine.GRID).data('kendoGrid')!;
            grid.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
        }
    }

    private updateCompanyName(symbol: string) {
        const success = (companyName: string) => {
            $(RelativeStrengthLine.COMPANY_NAME).html('&nbsp;&nbsp;' + companyName);
        };

        Ajax.invoke(
            GlobalData.URL_SUFFIX_GENERAL_DATA_COMPANY_NAME +
            '?symbol=' + symbol, 'GET', null, success);
    }

    private setDataSourceOptions(symbol: string) {
        if (symbol && symbol.length > 0) {
            this.dataSourceOptions = {
                transport: {
                    read: GlobalData.URL_BASE +
                        GlobalData.URL_SUFFIX_TECHNICAL_REPORTS_RELATIVE_STRENGTH_LINE +
                        '?symbol=' + symbol + '&timeSpan=' +
                        $(RelativeStrengthLine.NUM_WEEKS + ':checked').val(),
                },
            }
        }
    }

    private initChart(data: any) {
        const symbol = $(RelativeStrengthLine.SYMBOL).val()?.toString();

        const rsLine = new Array<ChartAxes>();
        const snpLine = new Array<ChartAxes>();
        let minRs = Number.MAX_VALUE;
        let maxRs = 0;
        let minSnp = Number.MAX_VALUE;
        let maxSnp = 0;

        for (const row of data) {
            rsLine.unshift(new ChartAxes(row.date, row.priceRelative));
            snpLine.unshift(new ChartAxes(row.date, row.indexClose));

            if (row.priceRelative < minRs) {
                minRs = row.priceRelative;
            }
            if (row.priceRelative > maxRs) {
                maxRs = row.priceRelative;
            }
            if (row.indexClose < minSnp) {
                minSnp = row.indexClose;
            }
            if (row.indexClose > maxSnp) {
                maxSnp = row.indexClose;
            }
        }
        maxRs = maxRs + (maxRs - minRs);
        minSnp = minSnp - (maxSnp - minSnp);

        minSnp = Math.floor(minSnp);
        maxSnp = Math.ceil(maxSnp);

        $(RelativeStrengthLine.CHART).kendoChart({
            title: {
                text: symbol + " Relative Strength Line vs. S&P 500 Index"
            },
            legend: {
                position: "bottom"
            },
            series: [{
                type: "line",
                markers: {
                    visible: false
                },
                data: rsLine,
                name: symbol + " Relative Strength Line",
                color: "blue",
                axis: "rs",
                field: "value",
                categoryField: "date",
                tooltip: {
                    visible: true,
                    template: "#= kendo.format('{0:MM/dd/yy}', category) #",
                }
            }, {
                type: "line",
                markers: {
                    visible: false
                },
                data: snpLine,
                name: "S&P 500 Index",
                color: "black",
                axis: "snp"
            }],
            valueAxis: [{
                name: "rs",
                color: "blue",
                min: minRs,
                max: maxRs,
                visible: false,
            }, {
                name: "snp",
                color: "black",
                min: minSnp,
                max: maxSnp
            }],
        });
    }
}
